import { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, ChevronRight, ExternalLink, Search, SlidersHorizontal,
  ArrowUpDown
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const badgeStyle = (theme) => {
  const map = { blue: 'bg-blue-50 text-blue-700', amber: 'bg-amber-50 text-amber-700', violet: 'bg-violet-50 text-violet-700', emerald: 'bg-emerald-50 text-emerald-700', rose: 'bg-rose-50 text-rose-700', sky: 'bg-sky-50 text-sky-700' };
  return map[theme] || 'bg-gray-50 text-gray-600';
};

function AdCard({ placement }) {
  const p = placement.partner;
  if (!p) return null;
  const size = placement.placement_size;
  const isLarge = size === 'large' || size === 'featured';
  const isMedium = size === 'medium';
  const colSpan = placement.grid_col_span > 1 ? `sm:col-span-${Math.min(placement.grid_col_span, 3)}` : '';
  const rowSpan = placement.grid_row_span > 1 ? `sm:row-span-${Math.min(placement.grid_row_span, 2)}` : '';

  const trackClick = () => {
    fetch(`${API}/partners/click/${p.partner_id}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ source_page: 'category', source_category_id: placement.category_id }) }).catch(() => {});
  };

  return (
    <Card className={`border-gray-100 shadow-sm hover:shadow-lg transition-all group overflow-hidden ${isLarge ? 'sm:col-span-2 sm:row-span-2' : isMedium ? 'sm:col-span-2' : ''} ${colSpan} ${rowSpan}`} data-testid={`ad-card-${p.slug}`}>
      <CardContent className={`${isLarge ? 'p-6' : 'p-4'} h-full flex flex-col`}>
        {p.logo_url && <img src={p.logo_url} alt={p.name} className="h-8 w-auto object-contain mb-2" />}
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className={`font-semibold text-[#0f1f33] group-hover:text-[#2563eb] transition-colors ${isLarge ? 'text-base' : 'text-sm'}`}>{p.name}</h3>
          {p.is_sponsored && <Badge className="bg-violet-50 text-violet-700 text-[10px] shrink-0">Sponsored</Badge>}
        </div>
        <p className={`text-gray-500 leading-relaxed mb-2 flex-1 ${isLarge ? 'text-sm' : 'text-xs'} line-clamp-3`}>{p.short_description}</p>
        <div className="flex flex-wrap gap-1 mb-2">
          {p.badges?.map((b, i) => <Badge key={i} className={`text-[10px] ${badgeStyle(b.color_theme)}`}>{b.label}</Badge>)}
        </div>
        {(p.discount_text || p.coupon_code) && (
          <div className={`rounded-lg p-2 mb-2 ${p.is_exclusive_discount ? 'bg-rose-50 border border-rose-100' : 'bg-emerald-50 border border-emerald-100'}`}>
            {p.discount_text && <p className="text-xs font-medium text-[#0f1f33]">{p.discount_text}</p>}
            {p.coupon_code && <p className="text-[10px] text-gray-500 mt-0.5">Code: <span className="font-mono font-semibold">{p.coupon_code}</span></p>}
          </div>
        )}
        <div className="flex gap-2 mt-auto">
          <Link to={`/partners/${p.slug}`} className="flex-1"><Button variant="outline" size="sm" className="w-full text-xs">Details</Button></Link>
          {p.affiliate_url && (
            <a href={p.affiliate_url} target="_blank" rel="noopener noreferrer" onClick={trackClick} className="flex-1">
              <Button size="sm" className="w-full bg-[#c41230] hover:bg-[#a00f28] text-white text-xs">{p.primary_cta_text || 'Visit Partner'} <ExternalLink className="w-3 h-3 ml-1" /></Button>
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function PartnersCategory() {
  const { slug, subcategorySlug } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [sort, setSort] = useState('recommended');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const subParam = subcategorySlug ? `&subcategory=${subcategorySlug}` : '';
    fetch(`${API}/partners/categories/${slug}?sort=${sort}${subParam}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug, subcategorySlug, sort]);

  if (loading) return <div className="max-w-6xl mx-auto px-4 py-12 space-y-4">{[...Array(4)].map((_, i) => <div key={i} className="h-32 bg-gray-50 rounded-xl animate-pulse border" />)}</div>;
  if (!data) return <div className="max-w-6xl mx-auto px-4 py-12 text-center text-gray-500">Category not found.</div>;

  const { category, subcategories, placements, total } = data;

  return (
    <div className="min-h-screen bg-[#fafbfc]" data-testid="partners-category-page">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-2 text-xs text-gray-400">
          <Link to="/" className="hover:text-[#2563eb]">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/partners" className="hover:text-[#2563eb]">Partners</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#0f1f33] font-medium">{category.name}</span>
          {subcategorySlug && data.subcategories && (
            <>
              <ChevronRight className="w-3 h-3" />
              <span className="text-[#0f1f33] font-medium">{subcategories.find(s => s.slug === subcategorySlug)?.name}</span>
            </>
          )}
        </div>
      </div>

      {/* Category Header */}
      <div className="bg-white border-b border-gray-100 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <Link to="/partners"><Button variant="ghost" size="sm" className="mb-3 -ml-2"><ArrowLeft className="w-4 h-4 mr-1" /> All Partners</Button></Link>
          <h1 className="text-2xl font-bold text-[#0f1f33] mb-2">{category.name}</h1>
          <p className="text-sm text-gray-500 max-w-2xl">{category.description}</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Subcategory chips */}
        {subcategories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="subcategory-chips">
            <Link to={`/partners/category/${slug}`}>
              <Badge className={`cursor-pointer px-3 py-1.5 text-xs ${!subcategorySlug ? 'bg-[#0f1f33] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>All</Badge>
            </Link>
            {subcategories.map((s, i) => (
              <Link key={i} to={`/partners/category/${slug}/${s.slug}`}>
                <Badge className={`cursor-pointer px-3 py-1.5 text-xs ${subcategorySlug === s.slug ? 'bg-[#0f1f33] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{s.name}</Badge>
              </Link>
            ))}
          </div>
        )}

        {/* Toolbar */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-gray-400">{total} partner{total !== 1 ? 's' : ''}</p>
          <div className="flex items-center gap-2">
            <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
            <select value={sort} onChange={e => setSort(e.target.value)} className="text-sm border-gray-200 rounded-md h-8 px-2" data-testid="sort-select">
              <option value="recommended">Recommended</option>
              <option value="featured">Featured</option>
              <option value="alphabetical">Alphabetical</option>
              <option value="newest">Newest</option>
              <option value="most_clicked">Most Clicked</option>
            </select>
          </div>
        </div>

        {/* Ad Grid */}
        {placements.length === 0 ? (
          <div className="text-center py-16">
            <Search className="w-10 h-10 text-gray-200 mx-auto mb-3" />
            <p className="text-sm text-gray-400">No partners in this category yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-auto" data-testid="ad-grid">
            {placements.map((pl, i) => <AdCard key={i} placement={pl} />)}
          </div>
        )}
      </div>
    </div>
  );
}
